import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "wouter";

import { tradingFiltersSchema } from "./model";
import { TradingCargoTable, TradingFilters } from "./ui";

import {
  useCancelOrderRequest,
  useGetCargoOrdersAllTrade,
  useGetTradeOrderStatuses,
  OrderPageAllTradeCargoResponseDTO,
} from "@/common/api";
import { CardContent, Card, TablePagination, CommentDialog } from "@/common/components";
import { ROUTES } from "@/common/config";
import { useToast, useUser } from "@/common/hooks";

const PAGE = 1;
const LIMIT = 10;

export default function TradingPage() {
  const { user } = useUser();
  const { toast } = useToast();
  const { data: statusesData, isLoading: isStatusesLoading } = useGetTradeOrderStatuses();

  const statuses = statusesData?.data.payload ?? null;
  const [, setLocation] = useLocation();

  const [statusId, setStatusId] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [createdAtError, setCreatedAtError] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState("");
  const [tradingStagesId, setTradingStagesId] = useState("");
  const [page, setPage] = useState(PAGE);

  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    requestId: string;
    action: "cancel" | "reject";
  }>({
    isOpen: false,
    requestId: "",
    action: "cancel",
  });

  const handleResetFilters = useCallback(() => {
    setStatusId("");
    setCreatedAt("");
    setCreatedAtError(null);
    setTradingStagesId("");
    setOrderNumber("");
    setPage(PAGE);
  }, []);

  const handleCreatedAtChange = useCallback((date: string) => {
    setCreatedAt(date);

    // Валидация даты
    const result = tradingFiltersSchema.safeParse({ createdAt: date });
    if (!result.success && result.error.errors[0]) {
      setCreatedAtError(result.error.errors[0].message);
    } else {
      setCreatedAtError(null);
    }
  }, []);

  // Мутация для отмены заявки
  const { mutateAsync: cancelOrderMutate } = useCancelOrderRequest();

  // Запрос сессий
  const {
    data: tradingRequestsData,
    isLoading: requestsLoading,
    refetch,
  } = useGetCargoOrdersAllTrade(
    {
      userId: user?.id!,
      statusId: statusId,
      date: createdAt,
      orderNumber: orderNumber,
      page,
      limit: LIMIT,
    },
    {
      query: {
        refetchInterval: 1 * 60 * 1000,
      },
    },
  );

  const handleCancel = useCallback(
    (comment: string) => {
      const requestId = cancelModal.requestId;

      cancelOrderMutate({
        data: {
          orderId: requestId,
          comment: comment.trim() || "",
        },
      })
        .then(() => refetch())
        .then(() => {
          toast({
            title: "Заявка отправлена на отмену",
            description: "Заявка успешно отправлена на отмену",
          });
          setCancelModal((prev) => ({ ...prev, isOpen: false }));
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка отмены заявки",
            description:
              error instanceof Error ? error.message : "Произошла ошибка при отмене заявки",
          });
        });
    },
    [cancelModal.requestId, cancelOrderMutate, refetch, toast],
  );

  const handleCancelAction = (requestId: string, action: "cancel" | "reject") => {
    if (action === "cancel" && requestId) {
      setCancelModal({
        isOpen: true,
        requestId,
        action,
      });
    }
  };

  const handleCancelModalClose = useCallback(() => {
    setCancelModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Сброс страницы при изменении фильтров
  useEffect(() => {
    setPage(PAGE);
  }, [statusId, createdAt, orderNumber, tradingStagesId]);

  const currentRequests = tradingRequestsData?.data?.payload?.items ?? [];
  const totalPages = tradingRequestsData?.data?.payload?.totalPage;
  const totalItems = tradingRequestsData?.data?.payload?.totalItems;

  // Находим заявку для отображения номера в модальном окне
  const cancelingRequest = currentRequests.find((req) => req.id === cancelModal.requestId);
  return (
    <div className="py-6">
      <div className="space-y-6">
        {/* Заголовок */}
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>
            Личный кабинет грузовладельца
          </h2>
          <p className="text-lg font-medium text-gray-700 mt-2">Все заявки</p>
        </div>

        <TradingFilters
          statuses={statuses}
          statusId={statusId}
          onStatusIdChange={(id) => setStatusId(id)}
          isStatusesLoading={isStatusesLoading}
          createdAt={createdAt}
          onCreatedAtChange={handleCreatedAtChange}
          createdAtError={createdAtError}
          orderNumber={orderNumber}
          onOrderNumberChange={(number) => setOrderNumber(number)}
          onReset={handleResetFilters}
        />

        <Card>
          <CardContent className="p-0 w-[calc(100vw-72px)] md:w-[calc(100vw-336px)]">
            <TradingCargoTable
              requests={currentRequests}
              isLoading={requestsLoading}
              onViewDetails={(request: OrderPageAllTradeCargoResponseDTO) => {
                if (request?.id) {
                  const basePath = ROUTES.ORDER_ID.replace(":id", String(request.id));
                  const participant = request.myParticipation ? "1" : "0";

                  // При переходе с TradingPage передаём в URL participant=1/0 в зависимости от myParticipation
                  setLocation(`${basePath}?participant=${participant}`);
                }
              }}
              onCancelRequest={(requestId: string) => handleCancelAction(requestId, "cancel")}
            />
          </CardContent>
        </Card>

        <div className="mt-6">
          <TablePagination
            currentPage={page}
            totalPages={totalPages ?? 0}
            totalItems={totalItems ?? 0}
            pageSize={LIMIT}
            onPageChange={(page) => {
              setPage(page);
            }}
          />
        </div>
      </div>
      <CommentDialog
        isOpen={cancelModal.isOpen}
        title="Отменить"
        description={`Вы уверены, что хотите отменить активность по заявке № ${cancelingRequest?.orderNumber || cancelModal?.requestId}?`}
        confirmText="Отменить"
        onConfirm={handleCancel}
        onCancel={handleCancelModalClose}
      />
    </div>
  );
}
